import { useState, useEffect } from 'react';
import { reviewsAPI } from '../api/vendly';
import { useAuth } from '../context/AuthContext';
import styles from './ReviewSection.module.css';

function Stars({ rating, interactive = false, onRate }) {
  const [hover, setHover] = useState(0);
  return (
    <div className={styles.stars}>
      {[1,2,3,4,5].map(n => (
        <button
          key={n}
          className={`${styles.star} ${n <= (hover || rating) ? styles.starFilled : ''}`}
          onClick={() => interactive && onRate && onRate(n)}
          onMouseEnter={() => interactive && setHover(n)}
          onMouseLeave={() => interactive && setHover(0)}
          style={{ cursor: interactive ? 'pointer' : 'default' }}
          type="button"
        >★</button>
      ))}
    </div>
  );
}

function RatingBar({ count, total, stars }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div className={styles.ratingBar}>
      <span className={styles.ratingBarLabel}>{stars}★</span>
      <div className={styles.ratingBarTrack}>
        <div className={styles.ratingBarFill} style={{ width: `${pct}%` }} />
      </div>
      <span className={styles.ratingBarCount}>{count}</span>
    </div>
  );
}

export default function ReviewSection({ productId }) {
  const { isLoggedIn, user } = useAuth();
  const [data,      setData]      = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [rating,    setRating]    = useState(0);
  const [title,     setTitle]     = useState('');
  const [body,      setBody]      = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error,     setError]     = useState('');

  const load = () => {
    reviewsAPI.list(productId)
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [productId]);

  const alreadyReviewed = data?.reviews?.some(r => r.userId === user?.id);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!rating) { setError('Please select a rating'); return; }
    setSubmitting(true); setError('');
    try {
      await reviewsAPI.add(productId, { rating, title, body });
      setShowForm(false); setRating(0); setTitle(''); setBody('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit review');
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Delete your review?')) return;
    await reviewsAPI.delete(reviewId);
    load();
  };

  if (loading) return <div className={styles.loading}><div className={styles.spin} /></div>;

  const { reviews = [], total = 0, average = 0, breakdown = {} } = data || {};

  return (
    <div className={styles.section}>
      <div className={styles.header}>
        <h2 className={styles.title}>Reviews {total > 0 && <span className={styles.count}>({total})</span>}</h2>
        {isLoggedIn && !alreadyReviewed && !showForm && (
          <button className={styles.writeBtn} onClick={() => setShowForm(true)}>
            Write a review
          </button>
        )}
      </div>

      {/* Summary */}
      {total > 0 && (
        <div className={styles.summary}>
          <div className={styles.summaryLeft}>
            <p className={styles.avgScore}>{average}</p>
            <Stars rating={Math.round(average)} />
            <p className={styles.avgLabel}>{total} review{total !== 1 ? 's' : ''}</p>
          </div>
          <div className={styles.summaryRight}>
            {[5,4,3,2,1].map(n => (
              <RatingBar key={n} stars={n} count={breakdown[n] || 0} total={total} />
            ))}
          </div>
        </div>
      )}

      {/* Write review form */}
      {showForm && (
        <form className={styles.form} onSubmit={handleSubmit}>
          <h3 className={styles.formTitle}>Your review</h3>
          {error && <p className={styles.formError}>{error}</p>}

          <div className={styles.formField}>
            <label className={styles.formLabel}>Rating *</label>
            <Stars rating={rating} interactive onRate={setRating} />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Title</label>
            <input
              className={styles.formInput}
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="Summarise your experience"
              maxLength={100}
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Review</label>
            <textarea
              className={styles.formTextarea}
              value={body}
              onChange={e => setBody(e.target.value)}
              placeholder="Tell other buyers what you think..."
              rows={3}
            />
          </div>

          <div className={styles.formBtns}>
            <button type="button" className={styles.cancelBtn} onClick={() => { setShowForm(false); setError(''); }}>
              Cancel
            </button>
            <button type="submit" className={styles.submitBtn} disabled={submitting || !rating}>
              {submitting ? 'Submitting…' : 'Submit review'}
            </button>
          </div>
        </form>
      )}

      {/* Reviews list */}
      {reviews.length === 0 && !showForm ? (
        <div className={styles.empty}>
          <span>⭐</span>
          <p>No reviews yet — be the first!</p>
          {isLoggedIn && !showForm && (
            <button className={styles.writeBtn} onClick={() => setShowForm(true)}>Write a review</button>
          )}
        </div>
      ) : (
        <div className={styles.list}>
          {reviews.map(r => (
            <div key={r.id} className={styles.reviewCard}>
              <div className={styles.reviewTop}>
                <div className={styles.reviewUser}>
                  <div className={styles.reviewAvatar}>{r.userName?.charAt(0).toUpperCase()}</div>
                  <div>
                    <p className={styles.reviewName}>{r.userName}</p>
                    <p className={styles.reviewDate}>{new Date(r.createdAt).toLocaleDateString('en-NG', { day:'numeric', month:'short', year:'numeric' })}</p>
                  </div>
                </div>
                <div className={styles.reviewRight}>
                  <Stars rating={r.rating} />
                  {r.userId === user?.id && (
                    <button className={styles.deleteBtn} onClick={() => handleDelete(r.id)}>Delete</button>
                  )}
                </div>
              </div>
              {r.title && <p className={styles.reviewTitle}>{r.title}</p>}
              {r.body  && <p className={styles.reviewBody}>{r.body}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
